import { useState, useCallback, useRef, useMemo, useEffect } from 'react';

type UnitField = 'amount' | 'discount';

export interface CellCoord {
  rankId: string | number;
  rangeKey: string;
  field: UnitField;
}

export interface AutofillSuggestion {
  value: number;
  direction: 'row' | 'column';
  unitType: UnitField;
  targetCell: CellCoord;
  allowOverwrite: boolean;
  /**
   * When set, only cells positioned after this cell (to the right for a row,
   * below for a column) are eligible targets. Left unset when the row/column
   * had no other populated cells, so the entire row/column stays eligible.
   */
  restrictAfter?: CellCoord;
}

interface EditedCell extends CellCoord {
  value: number;
}

interface UseAutofillParams {
  rankIds: Array<string | number>;
  rangeKeys: string[];
  getCell: (
    rankId: string | number,
    rangeKey: string
  ) => { amount: number; discount: number };
  updateCell: (
    rankId: string | number,
    rangeKey: string,
    field: UnitField,
    value: number
  ) => void;
  isDiscountReadOnly: (rangeKey: string) => boolean;
}

const BLUR_DELAY_MS = 50;

const areSameCell = (a: CellCoord | null, b: CellCoord | null): boolean =>
  a !== null &&
  b !== null &&
  a.rankId === b.rankId &&
  a.rangeKey === b.rangeKey &&
  a.field === b.field;

const useAutofill = ({
  rankIds,
  rangeKeys,
  getCell,
  updateCell,
  isDiscountReadOnly,
}: UseAutofillParams) => {
  const [focusedCell, setFocusedCell] = useState<CellCoord | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const lastEditedRef = useRef<EditedCell | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    },
    []
  );

  const findSoleNonZero = useCallback(
    (
      scope: 'row' | 'column',
      anchorRankId: string | number,
      anchorRangeKey: string,
      field: UnitField
    ): number | null => {
      let soleValue: number | null = null;

      if (scope === 'row') {
        for (const rk of rangeKeys) {
          if (field === 'discount' && isDiscountReadOnly(rk)) continue;

          const val = getCell(anchorRankId, rk)[field];

          if (val !== 0) {
            if (soleValue !== null) return null;
            soleValue = val;
          }
        }
      } else {
        for (const rid of rankIds) {
          if (field === 'discount' && isDiscountReadOnly(anchorRangeKey))
            continue;

          const val = getCell(rid, anchorRangeKey)[field];

          if (val !== 0) {
            if (soleValue !== null) return null;
            soleValue = val;
          }
        }
      }

      return soleValue;
    },
    [rankIds, rangeKeys, getCell, isDiscountReadOnly]
  );

  const hasOtherPopulatedCell = useCallback(
    (scope: 'row' | 'column', anchor: EditedCell): boolean => {
      if (scope === 'row') {
        return rangeKeys.some((rk) => {
          if (rk === anchor.rangeKey) return false;
          if (anchor.field === 'discount' && isDiscountReadOnly(rk))
            return false;

          return getCell(anchor.rankId, rk)[anchor.field] !== 0;
        });
      }

      return rankIds.some((rid) => {
        if (rid === anchor.rankId) return false;
        if (anchor.field === 'discount' && isDiscountReadOnly(anchor.rangeKey))
          return false;

        return getCell(rid, anchor.rangeKey)[anchor.field] !== 0;
      });
    },
    [rankIds, rangeKeys, getCell, isDiscountReadOnly]
  );

  const suggestion = useMemo((): AutofillSuggestion | null => {
    if (!focusedCell || isDismissed) return null;

    const source = lastEditedRef.current;

    if (
      source &&
      source.value !== 0 &&
      !areSameCell(source, focusedCell) &&
      source.field === focusedCell.field
    ) {
      const sameRow = source.rankId === focusedCell.rankId;
      const sameCol = source.rangeKey === focusedCell.rangeKey;

      if (sameRow || sameCol) {
        const direction: 'row' | 'column' = sameRow ? 'row' : 'column';
        const hasOtherValues = hasOtherPopulatedCell(direction, source);

        return {
          value: source.value,
          direction,
          unitType: source.field,
          targetCell: focusedCell,
          allowOverwrite: true,
          restrictAfter: hasOtherValues ? source : undefined,
        };
      }
    }

    const focusedValue = getCell(focusedCell.rankId, focusedCell.rangeKey)[
      focusedCell.field
    ];

    if (focusedValue !== 0) return null;

    const wasFillingColumn =
      source &&
      source.value !== 0 &&
      source.rangeKey === focusedCell.rangeKey &&
      source.field === focusedCell.field;

    const wasFillingRow =
      source &&
      source.value !== 0 &&
      source.rankId === focusedCell.rankId &&
      source.field === focusedCell.field;

    if (wasFillingColumn) {
      return {
        value: source.value,
        direction: 'column',
        unitType: focusedCell.field,
        targetCell: focusedCell,
        allowOverwrite: false,
      };
    }

    if (wasFillingRow) {
      return {
        value: source.value,
        direction: 'row',
        unitType: focusedCell.field,
        targetCell: focusedCell,
        allowOverwrite: false,
      };
    }

    const rowValue = findSoleNonZero(
      'row',
      focusedCell.rankId,
      focusedCell.rangeKey,
      focusedCell.field
    );

    if (rowValue !== null) {
      return {
        value: rowValue,
        direction: 'row',
        unitType: focusedCell.field,
        targetCell: focusedCell,
        allowOverwrite: false,
      };
    }

    const colValue = findSoleNonZero(
      'column',
      focusedCell.rankId,
      focusedCell.rangeKey,
      focusedCell.field
    );

    if (colValue !== null) {
      return {
        value: colValue,
        direction: 'column',
        unitType: focusedCell.field,
        targetCell: focusedCell,
        allowOverwrite: false,
      };
    }

    return null;
  }, [
    focusedCell,
    isDismissed,
    getCell,
    findSoleNonZero,
    hasOtherPopulatedCell,
  ]);

  const handleCellChange = useCallback(
    (
      rankId: string | number,
      rangeKey: string,
      field: UnitField,
      value: number
    ) => {
      lastEditedRef.current = { rankId, rangeKey, field, value };
      updateCell(rankId, rangeKey, field, value);
    },
    [updateCell]
  );

  const handleCellFocus = useCallback(
    (rankId: string | number, rangeKey: string, field: UnitField) => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      setFocusedCell({ rankId, rangeKey, field });
      setIsDismissed(false);
    },
    []
  );

  const handleCellBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      setFocusedCell(null);
      blurTimeoutRef.current = null;
    }, BLUR_DELAY_MS);
  }, []);

  const cancelBlur = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }, []);

  const applySuggestion = useCallback(() => {
    if (!suggestion) return;

    const { value, direction, unitType, allowOverwrite, restrictAfter } =
      suggestion;

    if (direction === 'row') {
      const { rankId } = suggestion.targetCell;
      const anchorIndex = restrictAfter
        ? rangeKeys.indexOf(suggestion.targetCell.rangeKey)
        : -1;

      for (const rk of rangeKeys) {
        if (unitType === 'discount' && isDiscountReadOnly(rk)) continue;
        if (restrictAfter && rangeKeys.indexOf(rk) < anchorIndex) continue;

        const current = getCell(rankId, rk)[unitType];

        if (current === value) continue;
        if (!allowOverwrite && current !== 0) continue;

        updateCell(rankId, rk, unitType, value);
      }
    } else {
      const { rangeKey } = suggestion.targetCell;
      const anchorIndex = restrictAfter
        ? rankIds.indexOf(suggestion.targetCell.rankId)
        : -1;

      for (const rid of rankIds) {
        if (unitType === 'discount' && isDiscountReadOnly(rangeKey)) continue;
        if (restrictAfter && rankIds.indexOf(rid) < anchorIndex) continue;

        const current = getCell(rid, rangeKey)[unitType];

        if (current === value) continue;
        if (!allowOverwrite && current !== 0) continue;

        updateCell(rid, rangeKey, unitType, value);
      }
    }

    lastEditedRef.current = null;
    setFocusedCell(null);
  }, [suggestion, rankIds, rangeKeys, getCell, updateCell, isDiscountReadOnly]);

  const dismissSuggestion = useCallback(() => {
    setIsDismissed(true);
  }, []);

  const isCellFocused = useCallback(
    (rankId: string | number, rangeKey: string, field: UnitField): boolean =>
      focusedCell !== null &&
      focusedCell.rankId === rankId &&
      focusedCell.rangeKey === rangeKey &&
      focusedCell.field === field,
    [focusedCell]
  );

  return {
    focusedCell,
    suggestion,
    handleCellChange,
    handleCellFocus,
    handleCellBlur,
    cancelBlur,
    applySuggestion,
    dismissSuggestion,
    isCellFocused,
  };
};

export default useAutofill;
